import React, { useEffect, useMemo, useState } from "react";
import { View, StyleSheet, Modal, ScrollView, TouchableOpacity } from "react-native";
import { Text, Button } from "react-native-paper";
import { theme } from "../theme/theme";
import Icon from "./Icon";
import {
  isAndroid,
  isLocalPushDisabled,
  isIos,
  isStandalonePwa,
  notificationsBlocked,
  prewarmPush,
  pushSupported,
  pushUnsupportedReason,
  subscribeToPush,
} from "../utils/pushRegistration";
import { canPromptInstall, promptInstall } from "../utils/installPrompt";

/**
 * Tells a member how to get alerts onto their phone.
 *
 * **iPhone and Android are not the same problem, and conflating them is how we
 * wasted a member's time on 6 Oct.** On an iPhone, push only works once the app
 * is on the Home Screen — in a Safari tab `PushManager.subscribe()` throws, so
 * install genuinely comes first. On Android, Chrome delivers push from an
 * ordinary tab, so demanding an install first is a wall we'd be building
 * ourselves. Installing is still nicer there, which makes it an offer, not a step.
 *
 *   blocked      they already refused — only the phone's own settings can undo it
 *   ios          iPhone, not installed — the one case where install is required
 *   unsupported  the browser can't do push at all, usually an in-app browser
 *   allow        ask for the permission, and offer the install alongside it
 *   done         nothing left to do
 *
 * `blocked` is checked first because nothing in here can fix it, and showing
 * setup steps to someone whose real problem is a refused permission wastes
 * their time — they tap the toggle, get the same refusal, and give up. Which is
 * exactly what happened.
 */
const InstallGuide = ({ visible, onDismiss }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [granted, setGranted] = useState(false);

  // Warmed on open so the permission prompt can be raised inside the tap on the
  // button below — the same constraint that broke the Settings toggle once.
  useEffect(() => {
    if (visible) {
      setError(null);
      prewarmPush();
    }
  }, [visible]);

  const state = useMemo(() => {
    if (!visible) return "done";
    if (granted) return "done";
    if (isLocalPushDisabled()) return "local";
    if (notificationsBlocked()) return "blocked";
    if (isIos() && !isStandalonePwa()) return "ios";
    if (!pushSupported()) return "unsupported";
    return "allow";
  }, [visible, granted]);

  const turnOnNotifications = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await subscribeToPush();
      setGranted(true);
    } catch (err) {
      setError(err?.message || "Couldn't turn notifications on.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Where a refused permission actually lives, which is not the same place on
   * every phone. On an installed app it's an app permission; in an Android
   * Chrome tab it's a per-site permission inside Chrome, and sending someone to
   * Settings > Apps for that is sending them somewhere Outback isn't listed.
   */
  const blockedSteps = () => {
    if (isIos()) {
      return [
        "Open your iPhone's Settings app",
        "Tap Notifications",
        "Find Outback in the list and tap it",
        "Turn on Allow Notifications",
        "Come back here and turn them on again",
      ];
    }
    if (isAndroid() && !isStandalonePwa()) {
      return [
        "In Chrome, tap the icon just left of the web address — a padlock or small sliders",
        "Tap Permissions, then Notifications",
        "Switch Notifications to Allow",
        "Reload the page and turn them on again",
      ];
    }
    return [
      "Open your phone's Settings app",
      "Tap Apps, then Outback",
      "Tap Notifications and allow them",
      "Come back here and turn them on again",
    ];
  };

  const blockedNote = () => {
    if (isIos()) {
      return "If Outback isn't in that list, press and hold its Home Screen icon, remove it, then add it again from Safari. That clears the refusal — it's the only way on an iPhone.";
    }
    if (isAndroid() && !isStandalonePwa()) {
      return "If Notifications isn't there, go to Chrome's menu, then Settings, Site settings, Notifications, and find outbackexchange.ai under Blocked.";
    }
    return null;
  };

  const install = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      animationType="slide"
      transparent
      visible={visible}
      onRequestClose={onDismiss}
    >
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <TouchableOpacity
            onPress={onDismiss}
            style={styles.closeIcon}
            accessibilityRole="button"
            accessibilityLabel="Close alert setup"
          >
            <Icon name="close" size={20} color={theme.colors.onSurface} />
          </TouchableOpacity>
          <ScrollView showsVerticalScrollIndicator={false}>
            {state === "done" && (
              <Content
                icon="check-circle"
                tint={theme.colors.eucalyptus}
                title="You're all set"
                body="Alerts will now come through to your phone, even with the app closed. A test alert is on its way. It may show up on your other phones too."
              />
            )}

            {state === "blocked" && (
              <Content
                icon="alert-circle"
                tint={theme.colors.warning}
                title="Notifications are switched off for Outback Exchange"
                body="Something said no to them once, and your phone remembers. We can't ask again from in here, but it takes a few seconds to undo:"
                steps={blockedSteps()}
                note={blockedNote()}
              />
            )}

            {state === "local" && (
              <Content
                icon="info"
                tint={theme.colors.eucalyptus}
                title="Alerts are available for mobile and tablet too."
                body="Open outbackexchange.ai in the browser on the device where you want alerts to show."
                note={
                  <>
                    <Text style={{ fontWeight: "700" }}>iPhone or iPad</Text>
                    {": Open outbackexchange.ai in Safari, under the three dots (…) tap Share → Add to Home Screen. Then you can open Outback Exchange from its new Home Screen icon, then tap Get alerts on your phone → Turn alerts on → Allow.\n\n"}
                    <Text style={{ fontWeight: "700" }}>Android, Android tablets and computers</Text>
                    {": In a supported browser, open outbackexchange.ai → Get alerts on your phone → Show me → Turn alerts on → Allow. Installation is optional. Alerts aren't available in this local preview."}
                  </>
                }
              />
            )}

            {state === "unsupported" && (
              <Content
                icon="alert-circle"
                tint={theme.colors.onSurfaceVariant}
                title="This browser can't do phone alerts"
                body={
                  pushUnsupportedReason() ||
                  "Open outbackexchange.ai in Chrome and you'll be able to turn them on there."
                }
                note="If you got here by tapping a link in Messenger, Facebook or Gmail, it opened inside that app's own browser, which can't send alerts. Open Chrome and go to outbackexchange.ai."
              />
            )}

            {state === "ios" && (
              <Content
                icon="share"
                tint={theme.colors.eucalyptus}
                title="Add Outback Exchange to your Home Screen first"
                body="On iPhone or iPad, alerts only work once the app is on your Home Screen. It's two taps, and the app opens faster afterwards too."
                steps={[
                  "Open outbackexchange.ai in Safari",
                  "Tap Share — the square with an arrow coming out of it",
                  "Scroll down and tap Add to Home Screen",
                  "Tap Add",
                  "Close Safari and open Outback Exchange from your new Home Screen icon",
                  "Then come back here and turn notifications on",
                ]}
                note="You have to open it from the Home Screen icon afterwards. Staying in Safari won't work — that's Apple's rule, not ours."
              />
            )}

            {state === "allow" && (
              <Content
                icon="bell"
                tint={theme.colors.eucalyptus}
                title={isIos() || isAndroid() ? "Turn on phone alerts" : "Turn on browser alerts"}
                body="Tap Turn alerts on, then choose Allow if this device asks for permission. Alerts will appear in this device’s notifications."
                note={
                  // Only worth saying where it's optional. On iPhone we never
                  // reach this state without the app already being installed.
                  canPromptInstall()
                    ? "You can also add Outback to your Home Screen, which makes it open faster. Alerts work either way."
                    : null
                }
              />
            )}

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <View style={styles.actions}>
              {state === "allow" && (
                <Button
                  mode="contained"
                  buttonColor={theme.colors.eucalyptus}
                  onPress={turnOnNotifications}
                  loading={busy}
                  disabled={busy}
                  accessibilityLabel="Turn on phone alerts"
                >
                  Turn alerts on
                </Button>
              )}

              {state === "allow" && canPromptInstall() && (
                <Button
                  mode="outlined"
                  textColor={theme.colors.eucalyptus}
                  onPress={install}
                  disabled={busy}
                  accessibilityLabel="Add Outback to your Home Screen"
                >
                  Add to Home Screen
                </Button>
              )}

              <TouchableOpacity
                onPress={onDismiss}
                style={[styles.close, state === "done" && styles.closeDone]}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={state === "done" ? "Close" : "Close, I'll do it later"}
              >
                <Text style={[styles.closeText, state === "done" && styles.closeTextDone]}>
                  {state === "done" ? "Done" : "I'll do it later"}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const Content = ({ icon, tint, title, body, steps, note }) => (
  <View>
    <View style={styles.headerRow}>
      <Icon name={icon} size={32} color={tint} />
    </View>
    <Text style={styles.title}>{title}</Text>
    <Text style={styles.body}>{body}</Text>

    {steps ? (
      <View style={styles.steps}>
        {steps.map((step, i) => (
          <View key={step} style={styles.step}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>{i + 1}</Text>
            </View>
            <Text style={styles.stepText}>{step}</Text>
          </View>
        ))}
      </View>
    ) : null}

    {note ? <Text style={styles.note}>{note}</Text> : null}
  </View>
);

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    justifyContent: "center",
    padding: theme.spacing.lg,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    maxHeight: "85%",
  },
  closeIcon: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  headerRow: {
    alignItems: "center",
    marginBottom: theme.spacing.sm,
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    fontFamily: "Poppins",
    color: theme.colors.onSurface,
    textAlign: "center",
    lineHeight: 27,
  },
  body: {
    fontSize: 14,
    fontFamily: "Poppins",
    color: theme.colors.onSurfaceVariant,
    textAlign: "center",
    marginTop: theme.spacing.sm,
    lineHeight: 21,
  },
  steps: {
    marginTop: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  step: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing.sm,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.colors.eucalyptus,
    alignItems: "center",
    justifyContent: "center",
  },
  stepNumberText: {
    fontSize: 13,
    fontWeight: "700",
    fontFamily: "Poppins",
    color: theme.colors.onPrimary,
  },
  stepText: {
    flex: 1,
    fontSize: 15,
    fontFamily: "Poppins",
    color: theme.colors.onSurface,
    lineHeight: 22,
  },
  note: {
    fontSize: 13,
    fontFamily: "Poppins",
    color: theme.colors.onSurfaceVariant,
    marginTop: theme.spacing.lg,
    lineHeight: 19,
  },
  error: {
    fontSize: 13,
    fontFamily: "Poppins",
    color: theme.colors.error,
    textAlign: "center",
    marginTop: theme.spacing.md,
  },
  actions: {
    marginTop: theme.spacing.xl,
    gap: theme.spacing.sm,
  },
  close: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
  },
  closeText: {
    fontSize: 15,
    fontFamily: "Poppins",
    color: theme.colors.onSurfaceVariant,
  },
  // "Done" is the one thing left to press, so it looks pressable: solid
  // eucalyptus, white text. As grey text it read as disabled.
  closeDone: {
    backgroundColor: theme.colors.eucalyptus,
    borderRadius: 22,
    alignSelf: "stretch",
  },
  closeTextDone: {
    color: theme.colors.onPrimary,
    fontWeight: "600",
  },
});

export default InstallGuide;
